import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { forgeClassHelper } from '../utils/classes';
import FormFieldLayout from '../FormFieldLayout';
import InlineMessage from '../InlineMessage';
import Input from '../Input';
import Label from '../Label';
import FormError from '../FormError';
import { FormContext } from '../Form/FormContext';

const classes = forgeClassHelper({ name: 'multi-field' });
const formFieldClasses = forgeClassHelper({ name: 'form-field' });

const MultiField = ({
  className,
  fields,
  hideRequiredStyles: hideRequiredStylesMF,
  hintText,
  labelAlwaysAbove,
  labelText,
  labelWidth,
  layout,
  nested,
  required,
  disabled,
  isLabelTextError = true,
  ...props
}) => {
  // if name is not provided for a field, populate it with the field's id
  const fieldsWithNames = fields.map((field) => {
    return {
      name: field.name || field.id,
      ...field,
    };
  });

  const fieldsHaveLabels = fields.some((field) => !!field.labelText);
  const hideLabelContainers = !fieldsHaveLabels;

  const calculatedLabelWidth = labelText ? (labelAlwaysAbove ? 12 : labelWidth) : 0;

  const showRequiredLabel = useCallback(
    (hideRequiredStyles, context) => {
      if (hideRequiredStyles) {
        // hideRequiredStyles is defined, use it
        return !hideRequiredStyles;
      } else if (hideRequiredStyles === undefined && context) {
        // hideRequiredStyles is undefined, use context (only 1 theme has req label)
        return context.requiredVariation === 'blueBarWithRequiredLabel';
      } else {
        return required;
      }
    },
    [required]
  );

  const fieldErrors = fields.map((field, index) => {
    const fieldProps = fieldsWithNames[index];
    return fieldProps.error ? (
      <div key={`${fieldProps.name}-error`}>
        <FormError {...classes('form-error')}>
          <span>
            {fieldProps.labelText && isLabelTextError
              ? `${fieldProps.labelText}: ${fieldProps.error}`
              : `${fieldProps.error}`}
          </span>
        </FormError>
      </div>
    ) : undefined;
  });

  return (
    <FormContext.Consumer>
      {(context) => (
        <div {...classes({ states: { disabled }, extra: className })}>
          <FormFieldLayout
            {...props}
            labelWidth={calculatedLabelWidth}
            nested={nested}
            layout={layout}
            className={className}
            statusExtras={
              formFieldClasses({
                element: 'status',
                modifiers: 'below',
                removeBase: true,
              }).className
            }
            useFieldset={!!labelText}
            labelSlot={
              labelText && (
                <Label
                  text={labelText}
                  required={showRequiredLabel(hideRequiredStylesMF, context)}
                  useLegend
                  disabled={disabled}
                />
              )
            }
            messageSlot={hintText && <InlineMessage text={hintText} disabled={disabled} />}
            statusIndicatorSlot={fieldErrors}
            inputSlot={
              <div {...classes({ element: 'fields-container' })}>
                {fields.map((field, index) => {
                  const fieldProps = fieldsWithNames[index];
                  const {
                    inputAs,
                    labelText,
                    id,
                    required,
                    disabled,
                    hideRequiredStyles: hideRequiredStylesField,
                    ...inputProps
                  } = fieldProps;
                  const InputComponent = inputAs || Input;
                  const hideRequiredStyles = hideRequiredStylesMF ? true : hideRequiredStylesField;

                  return (
                    <div {...classes({ element: 'field', modifiers: [id] })} key={id}>
                      <div
                        {...classes({
                          element: 'label-container',
                          modifiers: { empty: !labelText, hidden: hideLabelContainers },
                        })}
                      >
                        <Label
                          htmlFor={id}
                          text={labelText}
                          required={required && showRequiredLabel(hideRequiredStyles, context)}
                          disabled={disabled}
                          {...classes({
                            element: 'label',
                            modifiers: { empty: !labelText },
                          })}
                        />
                      </div>
                      <InputComponent
                        {...inputProps}
                        id={id}
                        required={required}
                        hideRequiredStyles={
                          hideRequiredStyles || (context && context.requiredVariation === 'allFieldsRequired')
                        }
                        disabled={disabled}
                      />
                    </div>
                  );
                })}
              </div>
            }
          />
        </div>
      )}
    </FormContext.Consumer>
  );
};

MultiField.defaultProps = {
  hideRequiredStyles: false,
  labelAlwaysAbove: false,
  required: false,
};

MultiField.propTypes = {
  /** Array of fields to render — for more information on these props, see the [FormField API Table](/components/formfield#api-tables) */
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * From FormField.PropTypes
       */
      /** Adds the relevant Forge state class and passes the prop down to the input */
      disabled: PropTypes.bool,
      /** Highlights the input when defined (specific string doesn't matter) */
      error: PropTypes.string,
      /** Sets the id attribute on the input and htmlFor on the label. */
      id: PropTypes.string.isRequired,
      /** The element type of the input. A React component class or function. Defaults to Input. */
      inputAs: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ render: PropTypes.func.isRequired })]),
      /** Sets the name attribute on to the input. Defaults to id. */
      name: PropTypes.string,
      /** Text of the label. */
      labelText: PropTypes.string,
      /** Handles the input reporting a change in its error status or message */
      onErrorChange: PropTypes.func,
      /** Indicates if it is a required field. */
      required: PropTypes.bool,
      /* Hides any required styling (currently a blue bar) */
      hideRequiredStyles: PropTypes.bool,
    })
  ).isRequired,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Disables all child elements when true. Note that you can also mark individual fields as disabled. */
  disabled: PropTypes.bool,
  /** If true, this will prevent required field styling from being applied to MultiField's label. This prop is passed to all fields within MultiField. */
  hideRequiredStyles: PropTypes.bool,
  /** Inline message displayed below the input fields */
  hintText: PropTypes.string,
  /** Makes label always go above input. Overrides `labelWidth`. */
  labelAlwaysAbove: PropTypes.bool,
  /** Adds a label that applies to all fields within this MultiField  */
  labelText: PropTypes.string,
  /** Width of the label in grid columns. Defaults to 3/12. */
  labelWidth: PropTypes.number,
  /**
   * Affects the vertical space that the fields occupy. Uses the 'medium' layout if unspecified.
   * If in a Form, overrides the 'layout' set at the Form level for this set of fields.
   */
  layout: PropTypes.oneOf(['super-compact', 'compact', 'medium', 'large']),
  /**
   * If true, form row removes standard grid row outer margin.
   * Set to false if the form row is at the top level of page layout.
   * Defaults to true.
   */
  nested: PropTypes.bool,
  /**
   * Function that takes a JS event as an argument. Captures bubbled events and is called when any field loses focus
   */
  onBlur: PropTypes.func,
  /**
   * Function that takes a JS event as an argument. Captures bubbled events and is called when any field's value changes
   */
  onChange: PropTypes.func,
  /**
   * Function that takes a JS event as an argument. Captures bubbled events and is called when any field receives focus
   */
  onFocus: PropTypes.func,
  /**
   * If true, this will add required field styling to the label of the entire MultiField (if `labelText` prop is provided).
   * To make fields within MultiField required, use the required prop on the individual fields provided in the `fields` prop of MultiField.
   */
  required: PropTypes.bool,
  /**
   * If true, component should prefix error messages with the given label text (when label text has been provided)
   *
   * The prop is defaulted to true
   */
  isLabelTextError: PropTypes.bool,
  /**
   * Props passed down to the `FormFieldLayout` component
   * */
  '...rest': PropTypes.any,
};

export default MultiField;
